import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, forwardRef, Input } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzUploadFile, NzUploadModule } from 'ng-zorro-antd/upload';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { MultipleFileUploadConfig } from '../../utils/multiple-file-upload';
import { RappiderButtonModule } from '../button/button.module';
import { ButtonSize } from '../../utils/button/button-size.enum';
import { ButtonType } from '../../utils/button/button-type.enum';
import { NzModalModule } from 'ng-zorro-antd/modal';
import { environment } from '@environment';

@Component({
  selector: 'rappider-multiple-file-upload',
  standalone: true,
  imports: [
    CommonModule,
    NzButtonModule,
    NzUploadModule,
    NzIconModule,
    RappiderButtonModule,
    NzModalModule,
    NzToolTipModule
  ],
  templateUrl: './multiple-file-upload.component.html',
  styleUrls: ['./multiple-file-upload.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => RappiderMultipleFileUploadComponent),
      multi: true
    }
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RappiderMultipleFileUploadComponent implements ControlValueAccessor {

  @Input() config: MultipleFileUploadConfig = {
    acceptedFileTypes: '.csv, .txt, .json, .png, .jpg, .jpeg, .pdf, .doc, .docx, .xls, .xlsx',
    uploadText: 'Upload',
    maxFileCount: environment.maxFileCount ?? 5,
    maxFileSize: environment.maxFileSize ?? 102400,
    showFileList: true,
    listType: 'picture-card',
    scrollable: false,
    clickable: false,
    allowDirectoryUpload: false,
    previewVisibility: true
  };

  @Input() previewImageInNewTab? = false;

  fileList: NzUploadFile[] = [];
  isDisabled = false;

  // Expose enums for template use
  ButtonSize = ButtonSize;
  ButtonType = ButtonType;

  showPreviewModal = false;
  previewImage: string | null = null;

  constructor(private cdr: ChangeDetectorRef) {
    // Constructor intentionally left empty
  }

  onChange: (value: File[] | null) => void = () => { /* intentionally empty */ };
  onTouched: () => void = () => { /* intentionally empty */ };

  writeValue(value: File[] | null): void {
    if (value && Array.isArray(value) && value.length > 0) {
      this.fileList = value.map((file, index) => ({
        uid: `${index + 1}`,
        name: file.name,
        status: 'done',
        size: file.size,
        type: file.type,
        originFileObj: file
      }));

      // Generate thumbnails for image files
      this.fileList.forEach(file => {
        if (file.originFileObj && this.isImageFile(file.originFileObj) && !file.thumbUrl) {
          this.generateThumbnail(file.originFileObj).then(thumbUrl => {
            file.thumbUrl = thumbUrl;
            this.cdr.detectChanges();
          }).catch(() => {
            this.cdr.detectChanges();
          });
        }
      });
    } else {
      this.fileList = [];
    }
  }

  /**
   * Registers a callback function to be called when the control's value changes.
   */
  registerOnChange(fn: (value: File[] | null) => void): void {
    this.onChange = fn;
  }

  /**
   * Registers a callback function to be called when the control is touched.
   */
  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  /**
   * Sets the disabled state of the control.
   */
  setDisabledState?(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
    this.cdr.detectChanges();
  }

  /**
   * Handles the file selection. Returns true to allow file to be added to list.
   * @param file The selected NzUploadFile.
   */
  beforeUpload = (file: NzUploadFile): boolean => {
    if (this.config.maxFileSize && file.size && file.size > this.config.maxFileSize * 1024) {
      const maxSizeMB = this.config.maxFileSize / 1024;
      console.warn(`File size exceeds ${maxSizeMB}MB limit`);
      return false;
    }

    if (this.config.maxFileCount && this.fileList.length >= this.config.maxFileCount) {
      console.warn(`Maximum ${this.config.maxFileCount} files allowed`);
      return false;
    }

    if (this.config.acceptedFileTypes) {
      const acceptedTypes = this.config.acceptedFileTypes.split(',').map(type => type.trim());
      const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
      if (!acceptedTypes.includes(fileExtension)) {
        console.warn(`File type ${fileExtension} is not accepted. Allowed types: ${this.config.acceptedFileTypes}`);
        return false;
      }
    }

    return true;
  };

  /**
   * Handles the removal of a file.
   */
  handleRemove = (): boolean => true;

  /**
   * Handle file removal from custom file list
   */
  handleRemoveFile = (file: NzUploadFile): void => {
    const index = this.fileList.findIndex(f => f.uid === file.uid);
    if (index > -1) {
      this.fileList.splice(index, 1);

      const files = this.fileList
        .filter(f => f.originFileObj && f.status === 'done')
        .map(f => f.originFileObj as File);

      this.onChange(files.length > 0 ? files : null);
      this.onTouched();
      this.cdr.detectChanges();
    }
  };

  /**
   * Custom request handler to prevent actual upload
   * This prevents the "Unknown Error" by handling the upload manually
   */
  customRequest = (item: any): any => { // eslint-disable-line @typescript-eslint/no-explicit-any
    setTimeout(() => {
      item.onSuccess({}, item.file);
    }, 0);
    return { unsubscribe: (): void => { /* intentionally empty */ } };
  };

  handlePreview(file: NzUploadFile): void {
    if (file.thumbUrl) {
      this.previewImage = file.thumbUrl;
      this.showPreviewModal = true;
    } else {
      this.previewImage = undefined;
      this.showPreviewModal = false;
    }
  }

  /**
   * Handle file click to open/preview file
   */
  handleFileClick = (file: NzUploadFile): void => {
    if (this.previewImageInNewTab) {
      if (file.originFileObj) {
        const url = URL.createObjectURL(file.originFileObj);
        window.open(url, '_blank');
      }
    } else {
      this.handlePreview(file);
    }
  };

  /**
   * Handles file list changes from ng-zorro
   * @param info Upload change information
   */
  handleChange = (info: { fileList: NzUploadFile[] }): void => {
    this.fileList = info.fileList || [];

    this.fileList.forEach(file => {
      if (file.originFileObj && this.isImageFile(file.originFileObj) && !file.thumbUrl) {
        this.generateThumbnail(file.originFileObj).then(thumbUrl => {
          file.thumbUrl = thumbUrl;
          this.cdr.detectChanges();
        }).catch(error => {
          console.warn('Failed to generate thumbnail:', error);
          this.cdr.detectChanges();
        });
      }
    });

    const files = this.fileList
      .filter(f => f.originFileObj && f.status === 'done')
      .map(f => f.originFileObj as File);

    this.onChange(files.length > 0 ? files : null);
    this.onTouched();
  };

  /**
   * Get the total size of all uploaded files
   */
  getTotalSize(): number {
    return this.fileList.reduce((total, file) => total + (file.size || 0), 0);
  }

  /**
   * Format file size for display
   */
  formatFileSize(bytes: number): string {
    if (bytes === 0) {
      return '0 Bytes';
    }
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  /**
   * Get appropriate icon for file type
   */
  getFileIcon(fileName: string): string {
    const extension = fileName.split('.').pop()?.toLowerCase();

    switch (extension) {
      case 'pdf':
        return 'file-pdf';
      case 'doc':
      case 'docx':
        return 'file-word';
      case 'xls':
      case 'xlsx':
        return 'file-excel';
      case 'ppt':
      case 'pptx':
        return 'file-ppt';
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif':
      case 'bmp':
        return 'file-image';
      case 'mp4':
      case 'avi':
      case 'mov':
        return 'file-video';
      case 'mp3':
      case 'wav':
      case 'flac':
        return 'file-audio';
      case 'zip':
      case 'rar':
      case '7z':
        return 'file-zip';
      case 'txt':
      case 'md':
        return 'file-text';
      case 'json':
      case 'xml':
        return 'file-code';
      default:
        return 'file';
    }
  }

  // // generate base64 for preview
  // getImageBase64(file: File): Promise<string> {
  //   return new Promise((resolve, reject) => {
  //     const reader = new FileReader();
  //     reader.readAsDataURL(file);
  //     reader.onload = () => resolve(reader.result as string);
  //     reader.onerror = error => reject(error);
  //   });
  // }

  /**
   * Track by function for ngFor
   */
  trackByUid(index: number, file: NzUploadFile): string {
    return file.uid;
  }

  /**
   * Check if file is an image
   */
  public isImageFile(file: File | null): boolean {
    return file ? file.type.startsWith('image/') : false;
  }

  /**
   * Formats file size from KB to readable format
   * @param sizeInKB File size in KB
   * @returns Formatted file size string
   */
  formatFileSizeForTooltip(sizeInKB: number): string {
    if (sizeInKB >= 1024) {
      return `${(sizeInKB / 1024).toFixed(0)} MB`;
    }
    return `${sizeInKB} KB`;
  }

  /**
   * Gets tooltip text for upload button
   * @returns Tooltip text with file count and size limits
   */
  getTooltipText(): string {
    const maxCount = this.config?.maxFileCount ?? environment.maxFileCount ?? 5;
    const maxSize = this.config?.maxFileSize ?? environment.maxFileSize ?? 102400;
    return `Maximum ${maxCount} file(s), ${this.formatFileSizeForTooltip(maxSize)} per file`;
  }

  /**
   * Generate thumbnail for image files
   */
  private generateThumbnail(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        resolve(e.target?.result as string);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

}
